/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: VIPListFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Call Center module.
----------------------------------------------------------------*/

import { useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Crown, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { ModuleFormHeader } from '../../components/layouts';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { VIPListMembersSection } from './VIPListMembersSection';
import { VIPListGeneralBox } from './components/VIPListGeneralBox';
import { useVIPListForm } from './hooks/useVIPListForm';
import { navigateToVipListDuplicate } from './vipListsActions';
import { buildApiUrl, getApiHeaders } from '../../config/api';

export default function VIPListFormView() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');

  const {
    formData,
    isLoading,
    isSaving,
    handleFieldChange,
    handleMembersChange,
    handleCancel,
    handleSaveWithAction,
  } = useVIPListForm({ id, duplicateId, isEditMode, navigate });

  // -- Quick List state ------------------------------------------
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListItems, setQuickListItems] = useState<Array<{ id: number; name: string }>>([]);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  const filteredQuickList = quickListSearch.trim()
    ? quickListItems.filter((item) => item.name.toLowerCase().includes(quickListSearch.trim().toLowerCase()))
    : quickListItems;

  const loadQuickList = async () => {
    if (quickListLoadingRef.current) return;
    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const params = new URLSearchParams({ page: '1', limit: '100', sortBy: 'name', sortOrder: 'asc' });
      const response = await fetch(buildApiUrl(`/api/telephony/vip-lists?${params.toString()}`), { headers: getApiHeaders() });
      const result = await response.json();
      setQuickListItems(Array.isArray(result?.data) ? result.data : []);
    } catch {
      setQuickListItems([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-12 space-y-4">
      <ModuleFormHeader
        icon={<Crown className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('vipLists.form.editTitle')} - ${formData.name || ''}` : t('vipLists.form.createTitle')}
        backButtonText={t('common.list')}
        onBack={() => navigate(-1)}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('vipLists.actions.quickList'),
            onClick: () => { setShowQuickList(true); void loadQuickList(); },
          },
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('vipLists.actions.duplicate'),
              onClick: () => { if (id) navigateToVipListDuplicate(navigate, id); },
            }]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('vipLists.actions.add'),
            onClick: () => navigate('/pbx/call-center/vip-lists/new'),
          }
        ]}
      />

      <div className="space-y-4 pb-10">
        <VIPListGeneralBox
          name={formData.name}
          enabled={formData.enabled}
          onNameChange={(v) => handleFieldChange('name', v)}
          onEnabledChange={(v) => handleFieldChange('enabled', v)}
          t={t}
        />

        {/* Members Section */}
        <VIPListMembersSection
          members={formData.members}
          onChange={handleMembersChange}
        />
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        isSaving={isSaving}
        isLoading={isLoading}
        isEditMode={isEditMode}
        saveText={t('common.save')}
        cancelText={t('common.cancel')}
        useSaveDropdown={true}
        saveDropdownStorageKey="vipLists:saveActionPreference"
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
          quickListLoadingRef.current = false;
        }}
        title={t('vipLists.quickList.title', 'VIP Lists')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/call-center/vip-lists/${(item as any).uuid || item.id}`, { replace: true });
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              setShowQuickList(false);
              setQuickListSearch('');
              navigate(`/pbx/call-center/vip-lists/${(item as any).uuid || item.id}`, { replace: true });
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">{item.name}</div>
          </button>
        )}
        searchPlaceholder={t('vipLists.quickList.searchPlaceholder', 'Search by name')}
        emptyMessage={t('vipLists.quickList.empty', 'No VIP lists found')}
        headerIcon={<Crown className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}
